import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, formatDate } from '../../utils/api'
import { MetricsRow } from '../shared'
import '../../styles/pages.css'

function PIMDimensions() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Stats
  const [stats, setStats] = useState({
    brand_count: 0,
    master_product_count: 0,
    variant_count: 0,
    needs_review_count: 0
  })

  // Dimension types data
  const [dimensionTypes, setDimensionTypes] = useState([])
  const [searchTerm, setSearchTerm] = useState('')

  // Add modal state
  const [showAddModal, setShowAddModal] = useState(false)
  const [newTypeName, setNewTypeName] = useState('')
  const [newTypeValues, setNewTypeValues] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    setLoading(true)
    setError(null)
    try {
      const [statsRes, typesRes] = await Promise.all([
        api.get('/api/pim/stats'),
        api.get('/api/pim/dimension-types')
      ])
      setStats(statsRes)
      setDimensionTypes(typesRes.dimension_types || [])
    } catch (err) {
      console.error('Failed to load data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Filter by search term
  const filteredTypes = dimensionTypes.filter(dt =>
    dt.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Calculate totals for metrics
  const totalTypes = dimensionTypes.length
  const totalValues = dimensionTypes.reduce((sum, dt) => sum + dt.value_count, 0)
  const totalUsage = dimensionTypes.reduce((sum, dt) => sum + dt.usage_count, 0)

  function handleRowClick(dimType) {
    navigate(`/pim/dimensions/${dimType.dimension_type_id}`)
  }

  async function handleAddType(e) {
    e.preventDefault()
    if (!newTypeName.trim()) {
      setError('Type name is required')
      return
    }

    setCreating(true)
    setError(null)

    try {
      // Parse comma-separated values
      const values = newTypeValues
        .split(',')
        .map(v => v.trim())
        .filter(v => v.length > 0)

      const response = await api.post('/api/pim/dimension-types', {
        name: newTypeName.trim(),
        values: values
      })
      setSuccess(`Dimension type "${response.name}" created with ${response.values.length} value(s)`)
      setNewTypeName('')
      setNewTypeValues('')
      setShowAddModal(false)
      // Reload data
      await loadData()
    } catch (err) {
      console.error('Failed to create dimension type:', err)
      setError(err.message)
    } finally {
      setCreating(false)
    }
  }

  const metrics = [
    { label: 'Dimension Types', value: totalTypes, color: 'blue' },
    { label: 'Total Values', value: totalValues, color: 'green' },
    { label: 'Variants Using', value: totalUsage, color: 'purple' },
    { label: 'Needs Review', value: stats.needs_review_count, color: stats.needs_review_count > 0 ? 'red' : '' }
  ]

  return (
    <>
      <MetricsRow metrics={metrics} />

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setError(null)}
            style={{ marginLeft: 'var(--space-2)' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div className="card" style={{ background: 'var(--color-success-light)', padding: 'var(--space-4)', marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          {success}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setSuccess(null)}
            style={{ marginLeft: 'var(--space-2)' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Search and Add */}
      <div className="card" style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="filter-group" style={{ flex: 1, minWidth: 250 }}>
            <input
              type="text"
              className="form-input"
              placeholder="Search dimension types..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            + Add Dimension Type
          </button>
        </div>
        {filteredTypes.length !== dimensionTypes.length && (
          <div className="text-sm text-muted" style={{ marginTop: 'var(--space-2)' }}>
            Showing {filteredTypes.length} of {dimensionTypes.length} types
          </div>
        )}
      </div>

      {/* Dimension Types Table */}
      <div className="card">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : filteredTypes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">
              {dimensionTypes.length === 0 ? 'No Dimension Types' : 'No Matching Types'}
            </div>
            <div className="empty-state-description">
              {dimensionTypes.length === 0
                ? 'Click "Add Dimension Type" to create your first type (e.g., Colour, Year, Finish).'
                : 'Try adjusting your search term.'
              }
            </div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th style={{ textAlign: 'center' }}>Values</th>
                <th style={{ textAlign: 'center' }}>Usage</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filteredTypes.map(dt => (
                <tr
                  key={dt.dimension_type_id}
                  onClick={() => handleRowClick(dt)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <span style={{ fontWeight: 500 }}>{dt.name}</span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`badge ${dt.value_count > 0 ? 'badge-info' : 'badge-neutral'}`}>
                      {dt.value_count}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`badge ${dt.usage_count > 0 ? 'badge-success' : 'badge-neutral'}`}>
                      {dt.usage_count} variant{dt.usage_count !== 1 ? 's' : ''}
                    </span>
                  </td>
                  <td className="text-muted">
                    {formatDate(dt.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Dimension Type Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Dimension Type</h3>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowAddModal(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleAddType}>
              <div className="modal-body">
                <div className="filter-group">
                  <label className="filter-label">Type Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newTypeName}
                    onChange={e => setNewTypeName(e.target.value)}
                    placeholder="e.g., Colour, Year, Finish"
                    autoFocus
                    required
                  />
                </div>
                <div className="filter-group" style={{ marginTop: 'var(--space-4)' }}>
                  <label className="filter-label">Initial Values (optional)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newTypeValues}
                    onChange={e => setNewTypeValues(e.target.value)}
                    placeholder="e.g., Black, White, Sunburst (comma-separated)"
                  />
                  <div className="text-sm text-muted" style={{ marginTop: 'var(--space-1)' }}>
                    Separate multiple values with commas. You can add more values later.
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddModal(false)
                    setNewTypeName('')
                    setNewTypeValues('')
                  }}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creating || !newTypeName.trim()}
                >
                  {creating ? 'Creating...' : 'Create Type'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal styles */}
      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal {
          background: var(--color-bg);
          border-radius: var(--radius-lg);
          width: 100%;
          max-width: 480px;
          box-shadow: var(--shadow-lg);
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-4);
          border-bottom: 1px solid var(--color-border);
        }
        .modal-header h3 {
          margin: 0;
        }
        .modal-body {
          padding: var(--space-4);
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: var(--space-2);
          padding: var(--space-4);
          border-top: 1px solid var(--color-border);
        }
      `}</style>
    </>
  )
}

export default PIMDimensions
